import ImageUrlBuilder from '@sanity/image-url'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { STACK_ACTIVITY_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import { default as LinkLocale } from '../../../modules/i18n/LinkLocale'
import client from '../../../modules/sanityClient'
import urls from '../../../modules/urls'
import { useBlogStack } from '../../../pages/[lang]/webview/blog'
import { useHelpCenterStack } from '../../../pages/[lang]/webview/help-center'
import { useSaveScroll } from '../../../store/useScrollRestoreOnBack'

function urlFor(source: string) {
  const builder = ImageUrlBuilder(client)
  return builder.image(source)
}

interface BlogCardType {
  category: string
  subCategory: string
  title: string
  subtitle
  thumbnail
  slug
  tagLists
  isMobile?: boolean
  isSecretTab?: boolean
}
const BlogCard: React.FC<BlogCardType> = ({
  title,
  subtitle,
  thumbnail,
  slug,
  tagLists,
  category,
  subCategory,
  isMobile = false,
  isSecretTab = false,
}) => {
  const router = useRouter()
  const isNewPortal = useMemo(() => router.asPath?.includes('/portal/'), [router.asPath])
  const { useBlogFlow } = useBlogStack()
  const { push } = useBlogFlow()
  const appRouter = useAppRouter({ stackPush: push })

  const { useHelpCenterFlow } = useHelpCenterStack()
  const { push: helpPush } = useHelpCenterFlow()
  const helpRouter = useAppRouter({ stackPush: helpPush })
  const { saveScroll } = useSaveScroll()

  const webURL = isNewPortal
    ? urls.student.newPortal.helpCenter.detail(slug)
    : isSecretTab
    ? urls.student.landing.blog.live(slug)
    : urls.student.landing.blog.detail(slug)

  const handleClickCard = () => {
    // 스크롤 저장
    saveScroll()

    router.asPath?.includes('help-center')
      ? helpRouter.push(webURL, {
          stackActivity: STACK_ACTIVITY_NAME.HELP_CENTER_DETAIL_ACTIVITY,
          stackSendData: {
            slug: slug,
          },
        })
      : appRouter.push(webURL, {
          stackActivity: STACK_ACTIVITY_NAME.BLOG_DETAIL_ACTIVITY,
          stackSendData: {
            slug: slug,
          },
        })
  }
  return (
    <div onClick={() => handleClickCard()}>
      <div className={style(isMobile).cardWrapper}>
        <div className={style(isMobile).thumbnailWrapper}>
          {isMobile ? (
            <img
              alt={thumbnail.alt || ' '}
              loading="lazy"
              className="h-full w-full object-cover"
              src={urlFor(thumbnail).url()}
            />
          ) : (
            <Image
              alt={thumbnail.alt || ' '}
              loading="lazy"
              className="mx-auto aspect-[1.7083/1] object-cover"
              src={urlFor(thumbnail).url()}
              fill
              quality={100}
            />
          )}
        </div>
        <div className={style(isMobile).descriptionSectionWrapper}>
          <div className={style(isMobile).descriptionSection}>
            <div>
              <div className={style(isMobile).descriptionCategoryWrapper}>
                <div className="">{category} </div>
                <div className="border-r-1 mx-[12px] h-[12px]"></div>
                <div className="">{subCategory} </div>
              </div>

              <div className={style(isMobile).desciprtionTitle}>{title}</div>
              {subtitle && <span className={style(isMobile).descriptionSubtitle}>{subtitle}</span>}
            </div>
            <div className={style(isMobile).desciprtionTagSection}>
              {tagLists?.map((tagItem, i) => (
                <LinkLocale key={i} href={urls.student.landing.blog.tags(tagItem)}>
                  <div className={style(isMobile).desciprtionTags} key={i}>
                    {tagItem}
                  </div>
                </LinkLocale>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const style = (isMobile) =>
  isMobile
    ? {
        cardWrapper: `border-1 relative cursor-pointer overflow-hidden rounded-[8px] border-gray-200 bg-white`,
        thumbnailWrapper: `relative overflow-hidden bg-gray-300`,
        descriptionSectionWrapper: `h-[220px] px-[16px] pb-[16px] pt-[20px]`,
        descriptionSection: `flex h-full flex-col justify-between`,
        descriptionCategoryWrapper: `Body2-14Md flex flex-row items-center text-gray-700`,
        desciprtionTitle: `truncate-2-lines Title1-20Bd mt-[12px] mb-[8px]`,
        desciprtionTagSection: `flex flex-row gap-[8px]`,
        desciprtionTags: `Caption-12Bd flex rounded-[4px] bg-gray-100 py-[2px] px-[6px] text-gray-600`,
        descriptionSubtitle: `truncate-2-lines keep-all Body2-14Md min-h-[42px] text-gray-700`,
      }
    : {
        cardWrapper: `border-1 relative h-[432px] w-[336px] cursor-pointer overflow-hidden rounded-[8px] border-gray-200 bg-white`,
        thumbnailWrapper: `relative h-[196px] w-[336px] overflow-hidden bg-gray-300`,
        descriptionSectionWrapper: `h-[236px] px-[16px] pb-[16px] pt-[20px]`,
        descriptionSection: `flex h-full flex-col justify-between`,
        descriptionCategoryWrapper: `Body2-14Md flex flex-row items-center text-gray-700`,
        desciprtionTitle: `truncate-2-lines Headline2-24Bd  mt-[12px] mb-[8px] font-bold`,
        desciprtionTagSection: `flex flex-row gap-[8px]`,
        desciprtionTags: `Caption-12Bd flex rounded-[4px] bg-gray-100 py-[2px] px-[6px] text-gray-600`,
        descriptionSubtitle: `truncate-2-lines keep-all Body2-14Md min-h-[42px]  text-gray-700`,
      }
export default BlogCard
